import { useApolloClient, useMutation } from '@apollo/client';
import useMessages from 'hooks/useMessages';
import { DELETE_DOCUMENTS } from 'graphql/secure/mutations';
import { GET_DOCUMENTS } from 'graphql/secure/queries';

function useUploadDialogActions() {
  const { setSuccessMessage, setErrorMessage } = useMessages();
  const client = useApolloClient();

  const [deleteDocuments] = useMutation(DELETE_DOCUMENTS, {
    onError: () => setErrorMessage('There was an error removing your documents'),
    onCompleted: () => setSuccessMessage('Documents successfully removed'),
    update: (cache, { data: { deleteDocument } }) => {
      const documentsData = cache.readQuery({
        query: GET_DOCUMENTS,
      });
      const {
        documents: { edges, pageInfo },
      } = { documents: { edges: [], pageInfo: {}, ...documentsData } };
      const updatedDocuments = edges.filter(
        document => !deleteDocument.ids.includes(document.node.id)
      );

      if (updatedDocuments) {
        cache.writeQuery({
          query: GET_DOCUMENTS,
          data: {
            documents: {
              edges: updatedDocuments,
              pageInfo: pageInfo,
            },
          },
        });
      }
    },
    refetchQueries: ['GetParcelRiskMitigations'],
  });

  const handleSaveDocuments = async ({ documents, deletedDocuments }) => {
    if (deletedDocuments.length) {
      await deleteDocuments({
        variables: {
          ids: deletedDocuments,
        },
      });
    }

    if (documents.length) {
      const { cache } = client;
      const cacheEntry = cache.readQuery({
        query: GET_DOCUMENTS,
      });
      if (cacheEntry?.edges?.length) {
        const newDocuments = documents.map(document => ({
          __typename: 'DocumentEdge',
          node: document,
        }));

        if (newDocuments.length) {
          cache.writeQuery({
            query: GET_DOCUMENTS,
            data: {
              documents: {
                edges: [...newDocuments, ...cacheEntry.edges],
                pageInfo: cacheEntry.pageInfo,
              },
            },
          });
        }
      }
    }
  };

  const handleCancelEditing = async ({ documents, deletedDocuments }) => {
    if (deletedDocuments.length || documents.length) {
      const documentIds = documents.map(({ id }) => id);
      await deleteDocuments({
        variables: {
          ids: [...deletedDocuments, ...documentIds],
        },
      });
    }
  };

  return {
    handleCancelEditing,
    handleSaveDocuments,
    handleDeleteDocuments: deleteDocuments,
  };
}

export default useUploadDialogActions;
